import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetPickUpReserveListApi } from '../api/useGetPickUpReserveList';
import { usePickerSelect } from '../usePickerSelect';

interface RepairPickUpReserveStateHookProps {
  plantID: number;
  inspectionID: number;
}
export const useRepairPickUpReserveState = ({
  plantID,
  inspectionID,
}: RepairPickUpReserveStateHookProps) => {
  const { PickUpReserveListData } = useGetPickUpReserveListApi({
    plantID,
    inspectionID,
  });
  const [selectList, setSelectList] = useState(
    {} as {
      dateList: string[];
      timeList: string[];
    },
  );
  useEffect(() => {
    const { date_list, time_list } = PickUpReserveListData;
    const dateList = Array.isArray(date_list) ? date_list : [];
    const timeList = Array.isArray(time_list) ? time_list : [];
    setSelectList((prev) => ({
      ...prev,
      dateList,
      timeList,
    }));
  }, [PickUpReserveListData]);

  const { dateSelectList, timeSelectList } = useMemo(() => {
    return {
      dateSelectList: selectList.dateList,
      timeSelectList: selectList.timeList,
    };
  }, [selectList]);

  const firstPreferredDate = usePickerSelect({
    initialSelect: '',
    selectList: dateSelectList,
    pickerName: 'first-preferred-date',
  });
  const secondPreferredDate = usePickerSelect({
    initialSelect: '',
    selectList: dateSelectList,
    pickerName: 'second-preferred-date',
  });
  const thirdPreferredDate = usePickerSelect({
    initialSelect: '',
    selectList: dateSelectList,
    pickerName: 'third-preferred-date',
  });
  const firstPreferredTime = usePickerSelect({
    initialSelect: '',
    selectList: timeSelectList,
    pickerName: 'first-preferred-time',
  });
  const secondPreferredTime = usePickerSelect({
    initialSelect: '',
    selectList: timeSelectList,
    pickerName: 'second-preferred-time',
  });
  const thirdPreferredTime = usePickerSelect({
    initialSelect: '',
    selectList: timeSelectList,
    pickerName: 'third-preferred-time',
  });

  const getDateTimeInfo = useCallback((dateText: string, timeText: string) => {
    if (dateText !== '' && timeText !== '') {
      const dateInfo = new Date(`${dateText}T${timeText}:00+09:00`);
      return !!(dateInfo instanceof Date && !isNaN(dateInfo.getTime()))
        ? dateInfo
        : undefined;
    }
  }, []);

  const firstPreferredDateTime = useMemo(
    () =>
      getDateTimeInfo(
        firstPreferredDate.pickerSelect,
        firstPreferredTime.pickerSelect,
      ),
    [firstPreferredDate.pickerSelect, firstPreferredTime.pickerSelect],
  );
  const secondPreferredDateTime = useMemo(
    () =>
      getDateTimeInfo(
        secondPreferredDate.pickerSelect,
        secondPreferredTime.pickerSelect,
      ),
    [secondPreferredDate.pickerSelect, secondPreferredTime.pickerSelect],
  );
  const thirdPreferredDateTime = useMemo(
    () =>
      getDateTimeInfo(
        thirdPreferredDate.pickerSelect,
        thirdPreferredTime.pickerSelect,
      ),
    [thirdPreferredDate.pickerSelect, thirdPreferredTime.pickerSelect],
  );

  const disabled = useMemo(() => {
    // 必須項目チェック
    if (firstPreferredDateTime) {
      //XORチェック（入力途中の場合：false）
      const isIncompleteSecond =
        (secondPreferredDate.pickerSelect !== '') !==
        (secondPreferredTime.pickerSelect !== '');
      const isIncompleteThird =
        (thirdPreferredDate.pickerSelect !== '') !==
        (thirdPreferredTime.pickerSelect !== '');
      if (isIncompleteSecond || isIncompleteThird) {
        return !false;
      } else {
        // 重複チェック
        if (secondPreferredDateTime && thirdPreferredDateTime) {
          return !(
            !!(
              firstPreferredDateTime.getTime() !==
              secondPreferredDateTime.getTime()
            ) &&
            !!(
              secondPreferredDateTime.getTime() !==
              thirdPreferredDateTime.getTime()
            ) &&
            !!(
              thirdPreferredDateTime.getTime() !==
              firstPreferredDateTime.getTime()
            )
          );
        }
        if (secondPreferredDateTime) {
          return !(
            firstPreferredDateTime.getTime() !==
            secondPreferredDateTime.getTime()
          );
        }
        if (thirdPreferredDateTime) {
          return !(
            firstPreferredDateTime.getTime() !==
            thirdPreferredDateTime.getTime()
          );
        }
        // 第二、第三候補未入力の場合
        return !true;
      }
    } else {
      return !false;
    }
  }, [
    secondPreferredDate.pickerSelect,
    secondPreferredTime.pickerSelect,
    thirdPreferredDate.pickerSelect,
    thirdPreferredTime.pickerSelect,
    firstPreferredDateTime,
    secondPreferredDateTime,
    thirdPreferredDateTime,
  ]);

  return {
    disabled,
    firstPreferredDateTime,
    secondPreferredDateTime,
    thirdPreferredDateTime,
    firstPreferredDate,
    secondPreferredDate,
    thirdPreferredDate,
    firstPreferredTime,
    secondPreferredTime,
    thirdPreferredTime,
  };
};
